import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import PersonnelSearch from '../components/PersonnelSearch';
import useFetch from 'use-http';
import DialogList from 'src/dialogs';
import actions from 'src/actions';
import PersonnelNavBar from '../components/PersonnelNavBar';
import PersonnelToast from '../components/PersonnelToast';
import TSDILandman from 'src/models/personnel/TSDILandman';
import { IRootState } from 'src/reducers';

import '../BenefitStyleSheet.scss';
import InsuranceHistory from 'src/models/personnel/EmployeeInsuranceHistory';
import InsuranceHistoryEntry from 'src/models/personnel/InsuranceHistoryEntry';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import PersonnelAdvancedQuickFind from '../dialogs/PersonnelAdvancedQuickFindDialog';

enum TableViewTypes {
    Annual = 'Annual',
    Month = 'Month',
    Period = 'Pay Period',
    Day = 'Day'
}

class InsuranceHistoryCollection {
    annual: Array<InsuranceHistoryEntry>;
    month: Array<InsuranceHistoryEntry>;
    period: Array<InsuranceHistoryEntry>;
    day: Array<InsuranceHistoryEntry>;
}

const EmployeeInsuranceHistoryPage = () => {
    const { get, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const selectedPersonnel = useSelector((state: IRootState) => state.personnel.selectedPersonnel);
    const [allInsuranceHistory, setAllInsuranceHistory] = useState<Array<InsuranceHistory>>();
    const [filteredHistory, setFilteredHistory] = useState<Array<InsuranceHistoryEntry>>();
    const [selectedView, setSelectedView] = useState<TableViewTypes>(TableViewTypes.Annual);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const dispatch = useDispatch();

    // Initial render
    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    useEffect(() => {
        initializeData();
    }, [selectedPersonnel]);

    // simplifying the rows when a new filter is selected or data loads
    useEffect(() => {
        simplifyRows();
    }, [allInsuranceHistory, selectedView]);

    useEffect(() => {
        console.log('insurance is loading: ', isLoading);
    }, [isLoading]);

    const onCreate = () => {
        console.log('EmployeeInsuranceHistoryPage onCreate()');
        initializeData();
    };

    const onDestroy = () => {
        console.log('EmployeeInsuranceHistoryPage onDestroy()');
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    const displayTitle = () => {
        const title = selectedPersonnel ? `Insurance History - ${selectedPersonnel?.akaName ? selectedPersonnel?.akaName : selectedPersonnel?.fName + ' ' + selectedPersonnel?.lName}` : 'Insurance History';
        return title;
    };

    // gets all data per personnel selected
    const initializeData = async () => {
        if (!!selectedPersonnel) {
            setIsLoading(true);
            setAllInsuranceHistory(null);
            setSelectedView(TableViewTypes.Annual);
            getInsuranceHistory();
        }
    };

    const getInsuranceHistory = async () => {
        setIsLoading(true);
        let results = await get(`/v1.0/landmen/benefits/${selectedPersonnel.tsdNo}/get-insurance-history`);

        if (response.ok && results) {
            setAllInsuranceHistory(results);
            setIsLoading(false);
        } else {
            showToast('Unable to fetch insurance history data', `rgba(255,0,0,0.5)`);
        }
    };

    /**
     * Sets the selected Personnel to the store if the given ID is valid and it's not already the same personnel
     * @param personnel - personnel payload
     */
    const setSelectedPersonnel = (personnel: TSDILandman) => {
        if (personnel) {
            personnel?.lmNo !== selectedPersonnel?.lmNo
                ? dispatch({ type: '[personnel] SET_SELECTED_PERSONNEL', payload: personnel })
                : showToast('Selected personnel already set', `rgba(78,117,154,0.5)`);

        } else {
            showToast('Selected personnel is undefined', `rgba(255,0,0,0.5)`);
        }
    };

    // dialog start
    const toggleAdvancedQuickFind = () => {
        dispatch(actions.layout.showDialog(DialogList.PERSONNEL_ADVANCED_QUICK_FIND));
    };

    const hideDialog = () => {
        dispatch(actions.layout.hideDialog());
    };
    // dialog end

    // simplifying rows
    const simplifyRows = () => {
        let simplifiedRows: InsuranceHistoryCollection = new InsuranceHistoryCollection();
        simplifiedRows.annual = new Array<InsuranceHistoryEntry>();
        simplifiedRows.month = new Array<InsuranceHistoryEntry>();
        simplifiedRows.period = new Array<InsuranceHistoryEntry>();
        simplifiedRows.day = new Array<InsuranceHistoryEntry>();

        allInsuranceHistory?.forEach((entry) => {
            let annualRow: InsuranceHistoryEntry = new InsuranceHistoryEntry();
            annualRow.tsdNo = entry?.tsdNo;
            annualRow.akaName = entry?.akaName;
            annualRow.year = entry?.year;
            annualRow.annualPay = entry?.annualPay;

            // annual values
            annualRow.health = entry?.annualHealth;
            annualRow.employeeHealth = entry?.employeeAnnualHealth;
            annualRow.companyHealth = entry?.companyAnnualHealth;

            annualRow.dental = entry?.annualDental;
            annualRow.employeeDental = entry?.employeeAnnualDental;
            annualRow.companyDental = entry?.companyAnnualDental;

            annualRow.vision = entry?.annualVision;
            annualRow.employeeVision = entry?.employeeAnnualVision;
            annualRow.companyVision = entry?.companyAnnualVision;

            annualRow.life = entry?.annualLife;
            annualRow.employeeLife = entry?.employeeAnnualLife;
            annualRow.companyLife = entry?.companyAnnualLife;

            annualRow.disability = entry?.annualDisability;
            annualRow.employeeDisability = entry?.employeeAnnualDisability;
            annualRow.companyDisability = entry?.companyAnnualDisability;

            annualRow.total = entry?.annualTotal;
            annualRow.employeeTotal = entry?.employeeAnnualTotal;
            annualRow.companyTotal = entry?.companyAnnualTotal;

            simplifiedRows.annual = simplifiedRows.annual.concat(annualRow);

            // month values
            let monthRow: InsuranceHistoryEntry = new InsuranceHistoryEntry();
            monthRow.tsdNo = entry?.tsdNo;
            monthRow.akaName = entry?.akaName;
            monthRow.year = entry?.year;
            monthRow.annualPay = entry?.annualPay;

            monthRow.health = entry?.monthHealth;
            monthRow.employeeHealth = entry?.employeeMonthHealth;
            monthRow.companyHealth = entry?.companyMonthHealth;

            monthRow.dental = entry?.monthDental;
            monthRow.employeeDental = entry?.employeeMonthDental;
            monthRow.companyDental = entry?.companyMonthDental;

            monthRow.vision = entry?.monthVision;
            monthRow.employeeVision = entry?.employeeMonthVision;
            monthRow.companyVision = entry?.companyMonthVision;

            monthRow.life = entry?.monthLife;
            monthRow.employeeLife = entry?.employeeMonthLife;
            monthRow.companyLife = entry?.companyMonthLife;

            monthRow.disability = entry?.monthDisability;
            monthRow.employeeDisability = entry?.employeeMonthDisability;
            monthRow.companyDisability = entry?.companyMonthDisability;

            monthRow.total = entry?.monthTotal;
            monthRow.employeeTotal = entry?.employeeMonthTotal;
            monthRow.companyTotal = entry?.companyMonthTotal;

            simplifiedRows.month = simplifiedRows.month.concat(monthRow);

            // pay period values
            let periodRow: InsuranceHistoryEntry = new InsuranceHistoryEntry();
            periodRow.tsdNo = entry?.tsdNo;
            periodRow.akaName = entry?.akaName;
            periodRow.year = entry?.year;
            periodRow.annualPay = entry?.annualPay;

            periodRow.health = entry?.periodHealth;
            periodRow.employeeHealth = entry?.employeePeriodHealth;
            periodRow.companyHealth = entry?.companyPeriodHealth;

            periodRow.dental = entry?.periodDental;
            periodRow.employeeDental = entry?.employeePeriodDental;
            periodRow.companyDental = entry?.companyPeriodDental;

            periodRow.vision = entry?.periodVision;
            periodRow.employeeVision = entry?.employeePeriodVision;
            periodRow.companyVision = entry?.companyPeriodVision;

            periodRow.life = entry?.periodLife;
            periodRow.employeeLife = entry?.employeePeriodLife;
            periodRow.companyLife = entry?.companyPeriodLife;

            periodRow.disability = entry?.periodDisability;
            periodRow.employeeDisability = entry?.employeePeriodDisability;
            periodRow.companyDisability = entry?.companyPeriodDisability;

            periodRow.total = entry?.periodTotal;
            periodRow.employeeTotal = entry?.employeePeriodTotal;
            periodRow.companyTotal = entry?.companyPeriodTotal;

            simplifiedRows.period = simplifiedRows.period.concat(periodRow);

            // day values
            let dayRow: InsuranceHistoryEntry = new InsuranceHistoryEntry();
            dayRow.tsdNo = entry?.tsdNo;
            dayRow.akaName = entry?.akaName;
            dayRow.year = entry?.year;
            dayRow.annualPay = entry?.annualPay;

            dayRow.health = entry?.dayHealth;
            dayRow.employeeHealth = entry?.employeeDayHealth;
            dayRow.companyHealth = entry?.companyDayHealth;

            dayRow.dental = entry?.dayDental;
            dayRow.employeeDental = entry?.employeeDayDental;
            dayRow.companyDental = entry?.companyDayDental;

            dayRow.vision = entry?.dayVision;
            dayRow.employeeVision = entry?.employeeDayVision;
            dayRow.companyVision = entry?.companyDayVision;

            dayRow.life = entry?.dayLife;
            dayRow.employeeLife = entry?.employeeDayLife;
            dayRow.companyLife = entry?.companyDayLife;

            dayRow.disability = entry?.dayDisability;
            dayRow.employeeDisability = entry?.employeeDayDisability;
            dayRow.companyDisability = entry?.companyDayDisability;

            dayRow.total = entry?.dayTotal;
            dayRow.employeeTotal = entry?.employeeDayTotal;
            dayRow.companyTotal = entry?.companyDayTotal;

            simplifiedRows.day = simplifiedRows.day.concat(dayRow);
        });

        updateTableView(selectedView, simplifiedRows);
    };

    // filter
    const updateTableView = (view: TableViewTypes, data: InsuranceHistoryCollection) => {
        switch (view) {
            case TableViewTypes.Annual:
                setFilteredHistory(data?.annual);
                break;
            case TableViewTypes.Month:
                setFilteredHistory(data?.month);
                break;
            case TableViewTypes.Period:
                setFilteredHistory(data?.period);
                break;
            case TableViewTypes.Day:
                setFilteredHistory(data?.day);
                break;
            default:
                setFilteredHistory(null);
                break;
        }
    };

    const updateViewType = () => {
        let e = (document.getElementById('viewSelection')) as HTMLSelectElement;
        let option = e.options[e.selectedIndex];

        let view = option.value as TableViewTypes;
        setSelectedView(view);
    };

    return (
        <div>
            <PersonnelNavBar />
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <PersonnelSearch toggleAdvancedQuickFind={toggleAdvancedQuickFind} />
            <Page>
                <Page.Section width={14}>
                    <Panel backgroundColor="#D3D3D3">
                        <Panel panelTitle={displayTitle()}>
                            {isLoading && (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '50vh',
                                        width: '100%',
                                    }}
                                >
                                    <LoadingSpinner />
                                </div>
                            )}
                            {filteredHistory?.length > 0 &&
                                <div style={{ overflowY: 'scroll', maxHeight: '35rem' }}>
                                    <table style={{ width: '100%' }}>
                                        <thead>
                                            <tr style={{ background: '#d6d6d6' }}>
                                                <th style={{ width: '5%' }} className="sticky-header">Year</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Annual Salary</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Health Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Company Health Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Employee Health Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Vision Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Company Vision Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Employee Vision Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Dental Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Company Dental Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Employee Dental Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Life Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Company Life Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Employee Life Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Disability Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Company Disability Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Employee Disability Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Company Total</th>
                                                <th style={{ width: '5%' }} className="sticky-header">Employee Total</th>
                                            </tr>
                                        </thead>
                                        {filteredHistory?.map((history: InsuranceHistoryEntry, index: number) => {
                                            return (
                                                <tbody key="insurance-history-table">
                                                    <tr>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                                            {history.year}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.annualPay)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.health)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.employeeHealth)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.companyHealth)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.vision)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.companyVision)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.employeeVision)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.dental)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.companyDental)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.employeeDental)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.life)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.companyLife)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.employeeLife)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.disability)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.companyDisability)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.employeeDisability)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.total)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.companyTotal)}
                                                        </td>
                                                        <td style={{ width: '5%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'right' }}>
                                                            {new Intl.NumberFormat('en-US', {
                                                                style: 'currency',
                                                                currency: 'USD',
                                                            }).format(history?.employeeTotal)}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            );
                                        })}
                                    </table>
                                </div>}
                            {!selectedPersonnel && <h2 style={{ textAlign: 'center' }}>No personnel selected</h2>}
                            {((filteredHistory?.length <= 0 || !filteredHistory) && !isLoading) && <h3 style={{ textAlign: 'center' }}>No history found</h3>}
                            <hr />
                            <br />
                            <div className="form-row">
                                <label style={{ paddingRight: '1rem' }}>View Period</label>
                                <select name="viewSelection" id="viewSelection" onChange={() => updateViewType()}>
                                    <option value={TableViewTypes.Annual}>Annual</option>
                                    <option value={TableViewTypes.Month}>Monthly</option>
                                    <option value={TableViewTypes.Period}>Pay Period</option>
                                    <option value={TableViewTypes.Day}>Day</option>
                                </select>
                            </div>
                        </Panel>
                    </Panel>
                </Page.Section>
            </Page>
            <PersonnelAdvancedQuickFind
                dialogName={DialogList.PERSONNEL_ADVANCED_QUICK_FIND}
                hideDialog={hideDialog}
                selectedPersonnel={selectedPersonnel}
                selectPersonnel={setSelectedPersonnel}
            />
        </div>
    );
};

export default EmployeeInsuranceHistoryPage;


// WEBPACK FOOTER //
// ./src/components/personnel/pages/EmployeeInsuranceHistoryPage.tsx